import { apiGet } from "./base";

// Machine-readable marker carried on a background-process history step (and used as the
// smart_summary_status websocket `status`) when the EMR data fetch lands, decoupled from the summary.
// The Django writer (api/background_process/utils.py DATA_READY_MARKER) emits the same string; keep them
// in sync. The polling fallback matches on this `code` rather than the human-readable `status` text.
export const DATA_READY_MARKER = "data_ready";

// Type definitions for background processes
export interface BackgroundProcessHistory {
  status: string;
  // Optional machine-readable marker (e.g. DATA_READY_MARKER) so consumers can match a step
  // deterministically without coupling to the human-readable `status`.
  code?: string;
  [key: string]: unknown;
}

export interface BackgroundProcess {
  id: string | number;
  patient_id: number | string;
  name: string;
  state: string;
  is_active: boolean;
  error?: string | null;
  created_at: string;
  history?: BackgroundProcessHistory[];
  [key: string]: unknown;
}

export interface BackgroundProcessResponse {
  count: number;
  results: BackgroundProcess[];
}

const inFlightRequests = new Map<string, Promise<BackgroundProcessResponse>>();

export const fetchBackgroundProcesses = async (
  patientId: string | number,
  isActive: boolean | null = null
): Promise<BackgroundProcessResponse> => {
  const params = new URLSearchParams();
  params.append("patient_id", patientId.toString());

  if (isActive !== null) {
    params.append("is_active", isActive.toString());
  }

  const url = `/api/background_processes/?${params.toString()}`;
  const cacheKey: string = url;

  // A second layer of defense against high latency network requests
  // that tanstack was not defending against.
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)!;
  }

  const promise = apiGet<BackgroundProcessResponse>(url).then((response) => response.data);
  inFlightRequests.set(cacheKey, promise);

  promise.finally(() => {
    inFlightRequests.delete(cacheKey);
  });

  return promise;
};

export const fetchActiveBackgroundProcesses = async (patientId: string | number): Promise<BackgroundProcessResponse> => {
  return fetchBackgroundProcesses(patientId, true);
};

export const fetchAllBackgroundProcesses = async (patientId: string | number): Promise<BackgroundProcessResponse> => {
  return fetchBackgroundProcesses(patientId, null);
};

/**
 * Fetch background processes for multiple patients in a single request
 */
export const fetchBulkBackgroundProcesses = async (
  patientIds: Array<number | string>,
  isActive: boolean | null = null
): Promise<BackgroundProcessResponse> => {
  if (!patientIds || patientIds.length === 0) {
    return { count: 0, results: [] };
  }

  const params = new URLSearchParams();
  params.append("patient_ids", patientIds.join(","));

  if (isActive !== null) {
    params.append("is_active", isActive.toString());
  }

  const url = `/api/background_processes/?${params.toString()}`;
  const cacheKey: string = url;

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)!;
  }

  const promise = apiGet<BackgroundProcessResponse>(url).then((response) => response.data);
  inFlightRequests.set(cacheKey, promise);

  promise.finally(() => {
    inFlightRequests.delete(cacheKey);
  });

  return promise;
};

/**
 * Fetch active background processes for multiple patients
 */
export const fetchBulkActiveBackgroundProcesses = async (patientIds: Array<number | string>): Promise<BackgroundProcessResponse> => {
  return fetchBulkBackgroundProcesses(patientIds, true);
};
